<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	//Stores
	import { firmwares_gen_gble } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice, showBleControlSetupModal } from 'src/stores/features/ble/control-device';

	//Components
	import ModalPrompt from 'src/components/ModalPrompt.svelte';

	//Configs
	import { devices_config } from '@devices/index';

	import { Button, Alert, Modal, toast, Text, Loader, Container } from '@Shelly/shelly-design-system';
	import { for_update } from 'src/stores/device-runtime/firmware-ui';
	import { real_id } from 'src/helpers/interface_helpers';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';

	export let device;

	let showModal = false;
	let initialized = false;
	let checkResolved = false;
	let showWaitingModal = false;
	let clickChangeStatus = false;

	$: realDevice = $devices[real_id(device.id)];

	const semVer = /\d+(\.\d+){2,}/gi;

	$: statusStore = getDeviceStatus(device.id);
	$: versionInStatus = $statusStore?.['fwversion:0']?.version;

	$: currentFirmware =
		typeof realDevice?.fwID !== 'undefined' &&
		typeof realDevice?.fwID?.match(semVer)?.toString() !== 'undefined'
			? realDevice?.fwID?.match(semVer)?.toString()
			: typeof versionInStatus !== 'undefined' && versionInStatus !== ''
				? versionInStatus
				: undefined;

	$: hasUpdate =
		typeof $for_update?.[real_id(device.id)] !== 'undefined' &&
		$for_update?.[real_id(device.id)] !== versionInStatus;

	const handleCheckFWUpdate = () => {
		controlBleDevice.updateDeviceId(device.id);
		controlBleDevice.updateProcessRunning(true);
		clickChangeStatus = true;
		checkResolved = false;
	};

	const handleUpdate = () => {
		$os === 'IOS'
			? control_bluetooth_device_ios(real_id(device.id), 'update')
			: control_bluetooth_device_android(real_id(device.id), 'update');

		showWaitingModal = true;
	};

	$: if ($showBleControlSetupModal === 'update') {
		showWaitingModal = false;
	}

	$: switch ($controlBleDevice.currentStatusIndex) {
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
		case 2:
			if ($controlBleDevice.processRunning) {
				if (!checkResolved && clickChangeStatus) {
					if (hasUpdate) {
						showModal = true;
						firmwares_gen_gble.update(
							device.type,
							$for_update[real_id(device.id)]?.newFwVersion ??
								$controlBleDevice?.updateVersion,
						);
					} else {
						setTimeout(() => {
							$os === 'IOS'
								? control_bluetooth_device_ios(device.id, 'disconnect')
								: control_bluetooth_device_android(device.id, 'disconnect');
							controlBleDevice.reset();
						}, 100);
						toast.open({
							type: 'success',
							title: $format('device_is_updated', {
								default: 'Your device is up to date.',
							}),
						});
						controlBleDevice.updateProcessRunning(false);
					}

					clickChangeStatus = false;
					checkResolved = true;
				} else {
					controlBleDevice.updateProcessRunning(false);
				}
			}

			break;
		case 30:
			if (showWaitingModal) {
				showWaitingModal = false;
			}
			break;
		default:
			break;
	}

	$: switch ($controlBleDevice.currentStatusIndex) {
		// after receiving one of the statuses below remove the initialized state
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			initialized = false;
			break;
		// after receiving one of the statuses below add the initialized state
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
		case BLU_STATUS.DEVICE_PROCESSED:
		case BLU_STATUS.BEACON_ENABLED:
		case BLU_STATUS.BEACON_DISABLED:
		case BLU_STATUS.MOTION_LED_ENABLED:
		case BLU_STATUS.MOTION_LED_DISABLED:
		case BLU_STATUS.BLIND_TIME_SET:
		case BLU_STATUS.SENSITIVITY_SET:
		case BLU_STATUS.DISTANCE_RANGE_SET:
		case BLU_STATUS.INTERVAL_MEASURE_SET:
		case BLU_STATUS.MEASURE_VIBRATION_ENABLED:
		case BLU_STATUS.MEASURE_VIBRATION_DISABLED:
		case BLU_STATUS.MIN_ANGLE_THRESHOLD_SET:
		case BLU_STATUS.UTC_OFFSET_SET:
		case BLU_STATUS.TEMPERATURE_OFFSET_SET:
		case BLU_STATUS.HUMIDITY_OFFSET_SET:
		case BLU_STATUS.DARK_THRESHOLD_SET:
		case BLU_STATUS.BRIGHT_THRESHOLD_SET:
		case BLU_STATUS.INVERT_DISPLAY_ENABLED:
		case BLU_STATUS.INVERT_DISPLAY_DISABLED:
		case BLU_STATUS.FAHRENHEIT_ENABLED:
		case BLU_STATUS.FAHRENHEIT_DISABLED:
		case BLU_STATUS.CLOCK_SYNC_ENABLED:
		case BLU_STATUS.CLOCK_SYNC_DISABLED:
		case BLU_STATUS.POWER_SAVER_ENABLED:
		case BLU_STATUS.POWER_SAVER_DISABLED:
		case BLU_STATUS.ZIGBEE_ENABLED:
		case BLU_STATUS.ZIGBEE_DISABLED:
		case BLU_STATUS.SMOKE_THRESHOLD_SET:
		case BLU_STATUS.TEMPERATURE_THRESHOLD_SET:
		case BLU_STATUS.FIRE_GROUP_SET:
		case BLU_STATUS.CLOCK_MODE_SET:
			initialized = true;
			break;
		default:
			break;
	}

	const props = {
		title: `${$format('title_update_firmware_update', {
			default: 'Update Firmware',
		})}?`,
		value: '',
		button_1: $format('button_blu_trv_update_firmware', {
			default: 'Update firmware',
		}),
		button_1_variant: 'primary',
		button_2: $format('button_cancel', { default: 'cancel' }),
		callback: handleUpdate,
	};

	const closeWaitingModal = () => {
		showModal = false;
		showWaitingModal = false;
		checkResolved = true;
		controlBleDevice.updateProcessRunning(false);
	};
</script>

{#if !hasUpdate}
	{#if currentFirmware}
		{#key currentFirmware}
			<Alert
				type="success"
				class="mb-s"
				isHeadingBold
				heading={$format('device_is_updated', {
					default: 'Your device is up to date:',
				})}
				message={currentFirmware}
			/>
		{/key}
	{/if}
{:else}
	<Text styleType="body-small" class="mb-m ml-s">
		<span class="text-bold d-flex mb-xs"
			>{$format('span_current_firmware_version', {
				default: 'Current Firmware version:',
			})}
		</span>
		<span>{$for_update[device.id]?.fwVersion || $format('data_not_loaded')}</span>
	</Text>
	<Alert
		type="info"
		class="mb-s"
		customIcon="fa-solid fa-arrows-rotate text-primary"
		isHeadingBold
		heading={capFirstChar(
			$format('new_fw_update_header', {
				default: 'Firmware update available',
			}),
		)}
		message={$for_update[device.id]?.newFwVersion}
	/>
{/if}

<Button
	disabled={$controlBleDevice.processRunning || !initialized || !hasUpdate || !$app}
	size="m"
	width="full"
	on:click={() => handleCheckFWUpdate()}
>
	{capFirstChar($format('button_update', { default: 'update' }))}
</Button>

{#if showModal}
	<ModalPrompt bind:showModal {props}>
		<Text class="pb-0">
			{@html $format('new_firmware_version_available_prompt', {
				values: { device_type: devices_config[device.type].friendly_name },
				default: `A new Firmware version is available for your ‘<b>${
					devices_config[device.type].friendly_name
				}</b>’. Are you sure you want to proceed?`,
			})}
		</Text>
	</ModalPrompt>
{/if}

<!-- Alex: We need to move this to the BleControlModal Component somehow -->
{#if showWaitingModal}
	<Modal showModal={showWaitingModal} on:close={closeWaitingModal} title={props.title}>
		<svelte:fragment slot="content">
			<Container padding="0">
				<div class="d-flex align-items-center flex-direction-column">
					<Loader size="m" />

					<Text styleType="controls-small" class="mt-xs text-bold">
						{capFirstChar(
							$format('progress_state_initializing_update', {
								default: 'initializing update',
							}),
						)}...
					</Text>
				</div>

				<Alert
					type="info"
					class="mt-m"
					isHeadingBold
					heading={capFirstChar(
						$format('press_button_required', {
							default: 'Please press device button.',
						}),
					)}
					message={$format('alert_message_press_button_to_continue_update', {
						default:
							'Press the device button to wake it up and continue the firmware update.',
					})}
				/>
			</Container>
		</svelte:fragment>
	</Modal>
{/if}
